import type { CMYKColor } from "@pdfking/math"
import type { ColorID, ExtendedColor } from "@pdfking/renderer-api"
import type { PrimaryKey } from "@pdfking/templates"
import { RecordByPrimaryKey } from "@pdfking/templates"

import { OwnName } from "../utils/OwnName"

type ColorProps = Pick<ExtendedColor, "idml_model" | "idml_reserved" | "spot_name">

export type ColorEntry = PrimaryKey<ColorID> & CMYKColor & ColorProps

const Color = (
  id: ColorID,
  cyan: number,
  magenta: number,
  yellow: number,
  black: number,
  alpha = 1,
  props: ColorProps = {}
): ColorEntry => ({ id, cyan, magenta, yellow, black, alpha, ...props }) as ColorEntry

export const SECURITY_COLOR_ID = "security" as ColorID
export const CUT_COLOR_ID = "cut" as ColorID
export const BLEED_COLOR_ID = "bleed" as ColorID
export const PRIMARY_COLOR_ID = "primary" as ColorID
export const FOLD_COLOR_ID = "fold" as ColorID
export const CROSSMARK_COLOR_ID = "crossmark" as ColorID
export const PAPER_COLOR_ID = "paper" as ColorID
export const MEASURE_COLOR_ID = "measure" as ColorID
export const REGISTRATION_COLOR_ID = "registration" as ColorID
export const PREPRESS_BG_ID = "bg" as ColorID

// Process colors — also used as the first four slots of the colorbar cycle.
export const COLORBAR_ID_CYAN = "cyan" as ColorID
export const COLORBAR_ID_MAGENTA = "magenta" as ColorID
export const COLORBAR_ID_YELLOW = "yellow" as ColorID
export const COLORBAR_ID_BLACK = "black" as ColorID

// Colorbar-unique tints (bar01-04 = process CMYK above; bar06 = paper — reused).
export const COLORBAR_ID_WARM = "bar-warm" as ColorID // 0.5/0.4/0.4/0
export const COLORBAR_ID_K80 = "bar-k80" as ColorID
export const COLORBAR_ID_K40 = "bar-k40" as ColorID
export const COLORBAR_ID_C80 = "bar-c80" as ColorID
export const COLORBAR_ID_C40 = "bar-c40" as ColorID
export const COLORBAR_ID_M80 = "bar-m80" as ColorID
export const COLORBAR_ID_M40 = "bar-m40" as ColorID
export const COLORBAR_ID_Y80 = "bar-y80" as ColorID
export const COLORBAR_ID_Y40 = "bar-y40" as ColorID
export const COLORBAR_ID_BLUE = "bar-blue" as ColorID // C+M
export const COLORBAR_ID_GRAY = "bar-gray" as ColorID // 0.25/0.19/0.19/0
export const COLORBAR_ID_RED = "bar-red" as ColorID // M+Y
export const COLORBAR_ID_GREEN = "bar-green" as ColorID // C+Y

// The guides — drawn on non-printing layers only — are spot inks in the pdf
// too, named as ours like the layers: other tools (Illustrator) drop pdf
// layers, an ink still tells them apart. Measure stays process: the foldmarks are printed.
export const ColorList = RecordByPrimaryKey<ColorEntry>([
  Color(SECURITY_COLOR_ID, 0.6, 0.75, 0, 0.1, 1, {
    idml_model: "Spot",
    spot_name: OwnName("security"),
  }),
  Color(CUT_COLOR_ID, 0, 1, 1, 0.1, 1, { idml_model: "Spot", spot_name: OwnName("cut") }),
  Color(BLEED_COLOR_ID, 1, 0, 0, 0.1, 1, { idml_model: "Spot", spot_name: OwnName("bleed") }),
  Color(PRIMARY_COLOR_ID, 0.8, 0.5, 0, 0.3, 1),
  Color(FOLD_COLOR_ID, 1, 0, 1, 0.1, 1, { idml_model: "Spot", spot_name: OwnName("fold") }),
  // Crop/cut marks — resolves straight to InDesign's built-in Registration
  // swatch (prints on every separation plate) instead of a custom entry.
  Color(CROSSMARK_COLOR_ID, 0, 0, 0, 1, 1),
  // Offset plate signs/names — same built-in Registration swatch, different
  // call sites (see `GetShapesForOffsetNames`/`GetShapesForOffsetSigns`).
  Color(REGISTRATION_COLOR_ID, 1, 1, 1, 1, 1, {
    idml_model: "Registration",
    idml_reserved: "Registration",
  }),
  // Resolves to InDesign's built-in Paper swatch (no ink).
  Color(PAPER_COLOR_ID, 0, 0, 0, 0, 1, { idml_reserved: "Paper" }),
  Color(MEASURE_COLOR_ID, 0, 0, 0, 0.5, 1, { idml_model: "Spot" }),
  Color(PREPRESS_BG_ID, 0.1, 0, 0, 0),
  // Pure single-ink colorbar tiles — resolve to InDesign's own reserved
  // process inks (Cyan/Magenta/Yellow/Black), never to a custom swatch:
  // those four names can't be used for custom colors (IDML spec §6.2.1).
  Color(COLORBAR_ID_CYAN, 1, 0, 0, 0, 1),
  Color(COLORBAR_ID_MAGENTA, 0, 1, 0, 0, 1),
  Color(COLORBAR_ID_YELLOW, 0, 0, 1, 0, 1),
  Color(COLORBAR_ID_BLACK, 0, 0, 0, 1, 1, { idml_reserved: "Black" }),
  Color(COLORBAR_ID_WARM, 0.5, 0.4, 0.4, 0),
  Color(COLORBAR_ID_K80, 0, 0, 0, 0.8),
  Color(COLORBAR_ID_K40, 0, 0, 0, 0.4),
  Color(COLORBAR_ID_C80, 0.8, 0, 0, 0),
  Color(COLORBAR_ID_C40, 0.4, 0, 0, 0),
  Color(COLORBAR_ID_M80, 0, 0.8, 0, 0),
  Color(COLORBAR_ID_M40, 0, 0.4, 0, 0),
  Color(COLORBAR_ID_Y80, 0, 0, 0.8, 0),
  Color(COLORBAR_ID_Y40, 0, 0, 0.4, 0),
  Color(COLORBAR_ID_BLUE, 1, 1, 0, 0),
  Color(COLORBAR_ID_GRAY, 0.25, 0.19, 0.19, 0),
  Color(COLORBAR_ID_RED, 0, 1, 1, 0),
  Color(COLORBAR_ID_GREEN, 1, 0, 1, 0),
])
